import { useEffect, useState, useRef } from 'react';
import { Rule } from '../../types/Rules';

const isUUID = (str: string): boolean => {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return uuidRegex.test(str);
};

interface UseRuleEntitiesOptions<T> {
  rules: Rule[] | undefined;
  fields: Rule['type'][];
  ruleTypes: Rule['type'][];
  getValues: (rule: Rule) => string[]; // eslint-disable-line no-unused-vars
  fetchEntities: (uuids: string[]) => Promise<T[]>; // eslint-disable-line no-unused-vars
  getKey: (entity: T) => string; // eslint-disable-line no-unused-vars
}

interface UseRuleEntitiesResult<T> {
  entityMap: Record<string, T>;
  loading: boolean;
}

export function useRuleEntities<T>({
  rules,
  fields,
  ruleTypes,
  getValues,
  fetchEntities,
  getKey,
}: UseRuleEntitiesOptions<T>): UseRuleEntitiesResult<T> {
  const [entityMap, setEntityMap] = useState<Record<string, T>>({});
  const [loading, setLoading] = useState(true);
  const previousIdsRef = useRef<string>('');

  useEffect(() => {
    const load = async () => {
      const needsData = fields.some(f => ruleTypes.includes(f));

      if (!needsData) {
        setLoading(false);
        return;
      }

      if (!rules) {
        setLoading(false);
        return;
      }

      const matchingRules = rules.filter(r => ruleTypes.includes(r.type));
      const allValues = matchingRules.flatMap(getValues);
      const uuids = allValues.filter(isUUID);

      const currentIdsString = JSON.stringify(uuids.sort());

      if (currentIdsString === previousIdsRef.current) {
        return;
      }

      if (uuids.length === 0) {
        setLoading(false);
        previousIdsRef.current = currentIdsString;
        return;
      }

      previousIdsRef.current = currentIdsString;

      try {
        const entities = await fetchEntities(uuids);
        const map: Record<string, T> = {};
        entities.forEach(entity => {
          map[getKey(entity)] = entity;
        });
        setEntityMap(map);
      } catch {
        setEntityMap({});
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [rules, fields, ruleTypes, getValues, fetchEntities, getKey]);

  return { entityMap, loading };
}
